import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPanel() {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletePhone, setDeletePhone] = useState(null);
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBtn, setShowInstallBtn] = useState(false);
  const [stats, setStats] = useState({
    totalConversations: 0,
    totalMessages: 0,
    withSearchCriteria: 0,
    todayConversations: 0,
  });

  useEffect(() => {
    fetchConversations();
    
    // Listen for PWA install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallBtn(true);
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setShowInstallBtn(false);
    }
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setShowInstallBtn(false);
    }
    setDeferredPrompt(null);
  };

  const fetchConversations = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/api/conversations`);
      const data = response.data;
      setConversations(data);
      
      // Calculate stats
      const today = new Date().toISOString().split("T")[0];
      const totalMessages = data.reduce((acc, conv) => acc + conv.messages.length, 0);
      const withCriteria = data.filter((conv) => conv.search_criteria).length;
      const todayConvs = data.filter((conv) => 
        conv.created_at && conv.created_at.split("T")[0] === today
      ).length;
      
      setStats({
        totalConversations: data.length,
        totalMessages,
        withSearchCriteria: withCriteria,
        todayConversations: todayConvs,
      });
    } catch (err) {
      setError("Nie udało się pobrać rozmów");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteConversation = async () => {
    if (!deletePhone) return;
    try {
      await axios.delete(`${API_URL}/api/conversations/${encodeURIComponent(deletePhone)}`);
      setDeletePhone(null);
      setSelectedConversation(null);
      fetchConversations();
    } catch (err) {
      console.error("Failed to delete conversation:", err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return date.toLocaleString("pl-PL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return "—";
    return phone.replace(/(\+\d{2})(\d{3})(\d{3})(\d{3})/, "$1 $2 $3 $4");
  };

  const getStateBadge = (state) => {
    const stateMap = {
      'initial': { label: 'Nowy', color: 'text-blue-500 border-blue-500/50' },
      'gathering_criteria': { label: 'Zbieranie', color: 'text-yellow-500 border-yellow-500/50' },
      'links_sent': { label: 'Linki wysłane', color: 'text-purple-500 border-purple-500/50' },
      'awaiting_selection': { label: 'Oczekuje', color: 'text-orange-500 border-orange-500/50' },
      'selection_received': { label: 'Wybrano', color: 'text-cyan-500 border-cyan-500/50' },
      'handed_off': { label: 'Przekazano', color: 'text-emerald-500 border-emerald-500/50' },
    };
    return stateMap[state] || { label: state || 'Nieznany', color: 'text-zinc-500 border-zinc-500/50' };
  };

  const getLastMessage = (conv) => {
    if (!conv.messages || conv.messages.length === 0) return "Brak wiadomości";
    const lastMsg = conv.messages[conv.messages.length - 1];
    const content = lastMsg.content || "";
    return content.length > 60 ? content.substring(0, 60) + "..." : content;
  };

  const getLastActivity = (conv) => {
    if (!conv.messages || conv.messages.length === 0) return conv.created_at;
    const lastMsg = conv.messages[conv.messages.length - 1];
    return lastMsg.timestamp || conv.updated_at;
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 p-4 md:p-8 pb-20 md:pb-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-xl md:text-3xl font-bold text-white">
              Panel Administracyjny
            </h1>
            <p className="text-zinc-400 text-sm mt-1">
              Hellboy's Garage — WhatsApp Agent
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {showInstallBtn && (
              <Button 
                onClick={handleInstallClick}
                className="bg-orange-600 hover:bg-orange-700 text-white"
                data-testid="install-pwa-btn"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Zainstaluj
              </Button>
            )}
            <Button 
              variant="outline" 
              onClick={fetchConversations}
              className="border-zinc-700 hover:bg-zinc-800"
              data-testid="refresh-btn"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Odśwież
            </Button>
            <Button 
              variant="outline"
              onClick={() => window.location.href = "/"}
              className="border-zinc-700 hover:bg-zinc-800"
              data-testid="calculator-link"
            >
              Kalkulator
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <Card className="bg-zinc-900 border-zinc-800" data-testid="stat-total-conversations">
            <CardHeader className="pb-1 md:pb-2 p-3 md:p-6">
              <CardTitle className="text-xs md:text-sm font-medium text-zinc-400">
                Rozmowy
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 pt-0 md:p-6 md:pt-0">
              <div className="text-xl md:text-2xl font-bold text-white">{stats.totalConversations}</div>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-900 border-zinc-800" data-testid="stat-total-messages">
            <CardHeader className="pb-1 md:pb-2 p-3 md:p-6">
              <CardTitle className="text-xs md:text-sm font-medium text-zinc-400">
                Wiadomości
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 pt-0 md:p-6 md:pt-0">
              <div className="text-xl md:text-2xl font-bold text-white">{stats.totalMessages}</div>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-900 border-zinc-800" data-testid="stat-with-criteria">
            <CardHeader className="pb-1 md:pb-2 p-3 md:p-6">
              <CardTitle className="text-xs md:text-sm font-medium text-zinc-400">
                Z kryteriami
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 pt-0 md:p-6 md:pt-0">
              <div className="text-xl md:text-2xl font-bold text-emerald-500">{stats.withSearchCriteria}</div>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-900 border-zinc-800" data-testid="stat-today">
            <CardHeader className="pb-1 md:pb-2 p-3 md:p-6">
              <CardTitle className="text-xs md:text-sm font-medium text-zinc-400">
                Dzisiaj
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 pt-0 md:p-6 md:pt-0">
              <div className="text-xl md:text-2xl font-bold text-amber-500">{stats.todayConversations}</div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="grid md:grid-cols-3 gap-4 md:gap-6">
          {/* Conversations List */}
          <Card className="md:col-span-1 bg-zinc-900 border-zinc-800" data-testid="conversations-list">
            <CardHeader className="p-3 md:p-6">
              <CardTitle className="text-base md:text-lg text-white">Rozmowy</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {isLoading ? (
                <div className="p-4 text-center text-zinc-400">Ładowanie...</div>
              ) : error ? (
                <div className="p-4 text-center text-red-400">{error}</div>
              ) : conversations.length === 0 ? (
                <div className="p-4 text-center text-zinc-400">Brak rozmów</div>
              ) : (
                <ScrollArea className="h-[300px] md:h-[500px]">
                  <div className="divide-y divide-zinc-800">
                    {conversations.map((conv) => (
                      <button
                        key={conv.id}
                        onClick={() => {
                          setSelectedConversation(conv);
                          // On mobile, open the sheet
                          if (window.innerWidth < 768) {
                            setMobileDetailOpen(true);
                          }
                        }}
                        className={`w-full p-3 md:p-4 text-left hover:bg-zinc-800/50 transition-colors ${
                          selectedConversation?.id === conv.id ? "bg-zinc-800" : ""
                        }`}
                        data-testid={`conversation-item-${conv.phone_number}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-white text-xs md:text-sm">
                            {formatPhoneNumber(conv.phone_number)}
                          </span>
                          <div className="flex gap-1">
                            {conv.state && (
                              <Badge variant="outline" className={`${getStateBadge(conv.state).color} text-[10px] md:text-xs`}>
                                {getStateBadge(conv.state).label}
                              </Badge>
                            )}
                            {conv.search_criteria && !conv.state && (
                              <Badge variant="outline" className="text-emerald-500 border-emerald-500/50 text-[10px] md:text-xs">
                                Kryteria
                              </Badge>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-zinc-400 truncate">
                          {getLastMessage(conv)}
                        </p>
                        <p className="text-xs text-zinc-500 mt-1">
                          {formatDate(getLastActivity(conv))}
                        </p>
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>

          {/* Conversation Detail - Desktop Only */}
          <Card className="hidden md:block md:col-span-2 bg-zinc-900 border-zinc-800" data-testid="conversation-detail">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg text-white">
                {selectedConversation 
                  ? formatPhoneNumber(selectedConversation.phone_number)
                  : "Wybierz rozmowę"
                }
              </CardTitle>
              {selectedConversation && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setDeletePhone(selectedConversation.phone_number)}
                  data-testid="delete-conversation-btn"
                >
                  Usuń
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {!selectedConversation ? (
                <div className="h-[400px] flex items-center justify-center text-zinc-500">
                  Kliknij na rozmowę, aby zobaczyć szczegóły
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Selected Ads - if customer made selection */}
                  {selectedConversation.selected_ads && selectedConversation.selected_ads.length > 0 && (
                    <div className="bg-cyan-900/30 border border-cyan-700/50 rounded-lg p-4" data-testid="selected-ads-box">
                      <h4 className="text-sm font-medium text-cyan-400 mb-2">
                        Wybrane ogłoszenia ({selectedConversation.selected_ads.length})
                      </h4>
                      <div className="space-y-2">
                        {selectedConversation.selected_ads.map((link, idx) => (
                          <a 
                            key={idx}
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block text-xs text-cyan-300 hover:text-cyan-200 truncate underline"
                          >
                            {link}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Search Criteria */}
                  {selectedConversation.search_criteria && (
                    <div className="bg-zinc-800/50 rounded-lg p-4" data-testid="search-criteria-box">
                      <h4 className="text-sm font-medium text-emerald-500 mb-2">
                        Kryteria wyszukiwania
                      </h4>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {Object.entries(selectedConversation.search_criteria).map(([key, value]) => (
                          <div key={key} className="flex justify-between">
                            <span className="text-zinc-400 capitalize">
                              {key.replace(/_/g, " ")}:
                            </span>
                            <span className="text-white font-medium">{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Messages */}
                  <div>
                    <h4 className="text-sm font-medium text-zinc-400 mb-3">
                      Historia rozmowy ({selectedConversation.messages?.length || 0} wiadomości)
                    </h4>
                    <ScrollArea className="h-[350px] pr-4">
                      <div className="space-y-3">
                        {selectedConversation.messages?.map((msg, idx) => (
                          <div
                            key={idx}
                            className={`flex ${msg.role === "user" ? "justify-start" : "justify-end"}`}
                          >
                            <div
                              className={`max-w-[80%] rounded-lg px-4 py-2 ${
                                msg.role === "user"
                                  ? "bg-zinc-800 text-white"
                                  : "bg-emerald-900/50 text-emerald-50"
                              }`}
                              data-testid={`message-${idx}`}
                            >
                              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                              <p className="text-xs mt-1 opacity-60">
                                {formatDate(msg.timestamp)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="text-center text-zinc-500 text-xs md:text-sm">
          Hellboy's Garage © {new Date().getFullYear()}
        </div>
      </div>

      {/* Mobile Conversation Detail Sheet */}
      <Sheet open={mobileDetailOpen} onOpenChange={setMobileDetailOpen}>
        <SheetContent side="bottom" className="bg-zinc-900 border-zinc-800 h-[85vh] rounded-t-xl">
          <SheetHeader className="pb-4">
            <SheetTitle className="text-white flex items-center justify-between">
              <span>{selectedConversation ? formatPhoneNumber(selectedConversation.phone_number) : ""}</span>
              {selectedConversation && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => {
                    setDeletePhone(selectedConversation.phone_number);
                    setMobileDetailOpen(false);
                  }}
                >
                  Usuń
                </Button>
              )}
            </SheetTitle>
          </SheetHeader>
          
          {selectedConversation && (
            <div className="space-y-4 overflow-y-auto h-[calc(85vh-80px)]">
              {/* Selected Ads - if customer made selection */}
              {selectedConversation.selected_ads && selectedConversation.selected_ads.length > 0 && (
                <div className="bg-cyan-900/30 border border-cyan-700/50 rounded-lg p-3">
                  <h4 className="text-xs font-medium text-cyan-400 mb-2">
                    Wybrane ogłoszenia ({selectedConversation.selected_ads.length})
                  </h4>
                  <div className="space-y-1">
                    {selectedConversation.selected_ads.map((link, idx) => (
                      <a 
                        key={idx}
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-xs text-cyan-300 hover:text-cyan-200 truncate underline"
                      >
                        {link}
                      </a>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Search Criteria */}
              {selectedConversation.search_criteria && (
                <div className="bg-zinc-800/50 rounded-lg p-3">
                  <h4 className="text-xs font-medium text-emerald-500 mb-2">
                    Kryteria wyszukiwania
                  </h4>
                  <div className="grid grid-cols-2 gap-1 text-xs">
                    {Object.entries(selectedConversation.search_criteria).map(([key, value]) => (
                      <div key={key} className="flex justify-between">
                        <span className="text-zinc-400 capitalize">
                          {key.replace(/_/g, " ")}:
                        </span>
                        <span className="text-white font-medium">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Messages */}
              <div>
                <h4 className="text-xs font-medium text-zinc-400 mb-2">
                  Historia ({selectedConversation.messages?.length || 0})
                </h4>
                <div className="space-y-2">
                  {selectedConversation.messages?.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex ${msg.role === "user" ? "justify-start" : "justify-end"}`}
                    >
                      <div
                        className={`max-w-[85%] rounded-lg px-3 py-2 ${
                          msg.role === "user"
                            ? "bg-zinc-800 text-white"
                            : "bg-emerald-900/50 text-emerald-50"
                        }`}
                      >
                        <p className="text-xs whitespace-pre-wrap">{msg.content}</p>
                        <p className="text-[10px] mt-1 opacity-60">
                          {formatDate(msg.timestamp)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deletePhone} onOpenChange={() => setDeletePhone(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800 mx-4">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Usunąć rozmowę?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Czy na pewno chcesz usunąć rozmowę z {formatPhoneNumber(deletePhone)}? 
              Ta operacja jest nieodwracalna.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">
              Anuluj
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteConversation}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Usuń
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
